import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import AspectRatio from '../AspectRatio/AspectRatio';
import { getOptimisedSrcAttrs } from '../utils';

const bem = new BEMHelper('Image');

const TRANSPARENT_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

class ImageWithSkeleton extends PureComponent {
    static propTypes = {
        src: PropTypes.string,
        displaySrc: PropTypes.bool,
        computeAttributes: PropTypes.bool,
        alt: PropTypes.string,
        slowLoadingThresholdMs: PropTypes.number,
        aspectRatio: PropTypes.string,
        imageSrcsetSize: PropTypes.string,
        imageSrcset: PropTypes.string,
        noscript: PropTypes.bool,
        cover: PropTypes.bool,
        displayError: PropTypes.bool,
        height: PropTypes.number,
        width: PropTypes.number,
        noLogo: PropTypes.bool,
        withSkeleton: PropTypes.bool,
        srcset: {
            srcset: PropTypes.string,
            sizes: PropTypes.string,
            src: PropTypes.string,
        },
    };

    static defaultProps = {
        slowLoadingThresholdMs: 200,
        displaySrc: true,
        cover: false,
        computeAttributes: true,
        displayError: false,
        withSkeleton: true,
        src: TRANSPARENT_GIF,
    };

    constructor(props) {
        super(props);
        this.state = {
            skeleton: true,
            slowLoad: false,
            error: false,
        };
        this.computedAttributes = {};

        if (props.computeAttributes) {
            !props.imageSrcset && this.computeImageAttributes();
            !props.aspectRatio && this.computeAspectRatio();
        }
    }

    componentDidMount() {
        if (this.imageNode && this.imageNode.src && this.imageNode.complete) {
            this.setState({ imageStatus: 'loaded', skeleton: false });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.displaySrc !== this.props.displaySrc || nextProps.src !== this.props.src) {
            this.setState({
                skeleton: true,
                slowLoad: false,
            });

            clearTimeout(this.slowLoadCheck);
            this.slowLoadCheck = setTimeout(() => {
                if (!this.imageNode.complete) {
                    this.setState({
                        slowLoad: true,
                    });
                }
            }, this.props.slowLoadingThresholdMs);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.slowLoadCheck);
    }

    computeAspectRatio() {
        const { height, width } = this.props;

        if (width && height) {
            this.computedAttributes.aspectRatio = `${width}:${height}`;
        }
    }

    computeImageAttributes() {
        const { src } = this.props;

        if (src) {
            const optimisedSources = getOptimisedSrcAttrs(src);
            this.computedAttributes.src = optimisedSources.src;
            this.computedAttributes.srcSet = optimisedSources.srcSet;
        }
    }

    generateSrcSet(javaScriptEnabled = true) {
        const { displaySrc, imageSrcset, imageSrcsetSize, src } = this.props;

        if (!displaySrc && javaScriptEnabled) {
            // return transparent gif to hide image broken browser behaviour
            return {
                src: TRANSPARENT_GIF,
            };
        }

        return {
            src: this.computedAttributes.src || src || undefined,
            sizes: imageSrcsetSize || this.computedAttributes.sizes || undefined,
            srcSet: imageSrcset || this.computedAttributes.srcSet || undefined,
        };
    }

    generateNoScript() {
        const { alt, src } = this.props;
        const noScriptSrcSet = this.generateSrcSet(false);

        // For snapshots .html() will produce empty noscript tag. Component snapshots will include the extra img tag
        return (
            <noscript>
                <img alt={alt} className={bem('image', 'noScript')} src={src} {...noScriptSrcSet} />
            </noscript>
        );
    }

    render() {
        const { error, imageStatus, skeleton, slowLoad } = this.state;
        const { alt, className, cover, displayError, hideOnMobile, keepImageAspectRatio, lowQualitySrc, noLogo, noscript, withSkeleton } = this.props;
        const srcSet = this.generateSrcSet();
        const aspectRatio = this.computedAttributes.aspectRatio || this.props.aspectRatio;
        const errorModifier = noLogo ? null : error || displayError;
        const { aspectRatioClassNames, image, imageContainer = className } = className || {};
        const useSkeleton = withSkeleton ? skeleton : false;
        const status = useSkeleton ? 'loaded' : imageStatus;

        const contain = keepImageAspectRatio ? 'contain' : '';
        const containerOverride = contain && !skeleton;
        const containerSkeleton = contain && skeleton;

        return (
            <AspectRatio aspectRatio={aspectRatio} className={aspectRatioClassNames}>
                <div
                    className={bem(
                        'imageContainer',
                        { useSkeleton, containerOverride, containerSkeleton, error: errorModifier, [imageStatus || '']: true },
                        imageContainer,
                    )}
                >
                    {lowQualitySrc && <img className={bem('lowQuality')} src={lowQualitySrc} />}
                    <picture>
                        {hideOnMobile && <source media="(max-width: 1023px)" sizes="1px" srcSet={TRANSPARENT_GIF} />}
                        <source media={hideOnMobile ? '(min-width: 1024px)' : ''} sizes={srcSet.sizes} srcSet={srcSet.srcSet} />
                        <img
                            alt={status === 'loaded' || error ? alt : ''}
                            className={bem('image', { contain, useSkeleton, slowLoad, error: errorModifier, cover, [imageStatus]: true }, image)}
                            onError={this.handleImageErrored}
                            onLoad={withSkeleton ? this.removeSkeleton : undefined}
                            src={srcSet.src}
                            ref={(node) => {
                                this.imageNode = node;
                            }}
                        />
                    </picture>
                </div>
                {noscript && this.generateNoScript() /* Below imageContainer so renders on top of skeleton */}
            </AspectRatio>
        );
    }

    removeSkeleton = () => {
        this.setState({ skeleton: false, error: false, imageStatus: 'loaded' });
    };

    handleImageErrored = () => {
        this.setState({ error: true });
    };
}

export default ImageWithSkeleton;
